import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TRadioListOption } from '@treezor/design-system';

import { countriesCodes, TCountryCodes } from '@/utils/constants';

import {
  EIncomeRange,
  ELegalAnnualTurnOver,
  ELegalNetIncomeRange,
  ELegalNumberOfEmployeeRange,
  entityType,
  EOccupationType,
  EPersonalAssets,
  titles,
  legalForms,
  legalSectorTypes,
  entitySanctionsQuestionnaire,
  occupationCategoryValues,
  personalAssetsValues,
  monthlyIncomeRangeValues,
  sourceOfFunds,
  legalFormsIT,
  legalFormsDE,
  legalFormsES,
} from '@/features/user/store/user.model';

export function useFieldOptions() {
  const { t } = useTranslation();

  const countryCodeOptions = useMemo(
    () =>
      countriesCodes
        .map((key) => ({
          label: t(`shared.countries.${key}`),
          value: key,
        }))
        .sort((countryA, countryB) => (countryA.label <= countryB.label ? -1 : 1)),
    [t],
  );

  const legalFormOptions = useCallback((distributionCountry?: TCountryCodes) => {
    let options = Object.keys(legalForms).filter(
      (key) => !['1000', '5498', '5720', '6588', '10001', '20001', '30001'].includes(key),
    );

    if (distributionCountry === 'IT') {
      options = Object.keys(legalFormsIT).filter((key) => !['10001'].includes(key));
    }
    if (distributionCountry === 'DE') {
      options = Object.keys(legalFormsDE).filter((key) => !['20001'].includes(key));
    }
    if (distributionCountry === 'ES') {
      options = Object.keys(legalFormsES).filter((key) => !['30001'].includes(key));
    }

    return options
      .map((key) => ({
        label: `${key} - ${legalForms[key]}`,
        value: key,
      }))
      .sort((formA, formB) => (Number(formA.value) <= Number(formB.value) ? -1 : 1));
  }, []);

  const entityTypeOptions = useMemo(
    () =>
      Object.entries(entityType)
        .map(([key, value]) => ({
          label: t(`pages.currentProfiles.profile.kyc.entityTypes.${value}`),
          value: Number(key),
        }))
        .sort((countryA, countryB) => (countryA.label <= countryB.label ? -1 : 1)),
    [t],
  );

  const titleOptions = useMemo(
    () =>
      Object.entries(titles).map(([key, value]) => ({
        label: t(`pages.dashboard.users.titles.${value}`),
        value: key,
      })),
    [t],
  );

  const legalSectorTypeOptions: TRadioListOption[] = useMemo(
    () =>
      legalSectorTypes.map((sectorType) => ({
        label: sectorType,
        value: sectorType,
        testId: `radio-kyc-legal-sector${sectorType}`,
      })),
    [],
  );

  const incomeRangeOptions = useMemo(
    () =>
      Object.values(EIncomeRange).map((value) => ({
        label: t(`pages.dashboard.createUser.form.incomeRange.${value}`),
        value,
      })),
    [t],
  );

  const personalAssetOptions = useMemo(
    () =>
      Object.values(EPersonalAssets).map((value) => ({
        label: t(`pages.dashboard.createUser.form.personalAssets.${value}`),
        value,
      })),
    [t],
  );

  const legalAnnualTurnOverOptions: TRadioListOption[] = useMemo(
    () =>
      Object.values(ELegalAnnualTurnOver).map((range) => ({
        label: t(`pages.dashboard.createUser.form.legalAnnualTurnOvers.${range}`),
        value: range,
        testId: `radio-kyc-legal-annual-turnover-${range}`,
      })),
    [t],
  );

  const legalNetIncomeRangeOptions: TRadioListOption[] = useMemo(
    () =>
      Object.values(ELegalNetIncomeRange).map((value) => ({
        label: t(`pages.dashboard.createUser.form.legalNetIncomeRanges.${value}`),
        value,
        testId: `radio-kyc-legal-net-income-range-${value}`,
      })),
    [t],
  );

  const legalNumberOfEmployeeRangeOptions = useMemo(
    () =>
      Object.values(ELegalNumberOfEmployeeRange).map((range) => ({
        label: range,
        value: range,
      })),
    [],
  );

  const sanctionEmbargoQuestionaireOptions = useMemo(
    () =>
      Object.values(entitySanctionsQuestionnaire).map((questionKey) => ({
        label: t(`pages.dashboard.createUser.form.entitySanctionsQuestions.${questionKey}`),
        value: questionKey,
      })),
    [t],
  );

  const persoAndBusinessAddressOptions: TRadioListOption[] = useMemo(
    () =>
      ['yes', 'no'].map((value) => ({
        label: t(`pages.dashboard.createUser.form.isSameAsBusinessAddress.${value}`),
        value,
        testId: `radio-kyc-is-same-as-bu-address-${value}`,
      })),
    [t],
  );

  const persoAndParentAddressOptions = useMemo(
    () =>
      ['yes', 'no'].map((value) => ({
        label: t(`pages.dashboard.createUser.form.isSameAsParentAddress.${value}`),
        value,
      })),
    [t],
  );

  const userHierarchicalRelationOptions = useMemo(
    () =>
      ['no', 'yes'].map((value) => ({
        label: t(`pages.dashboard.createUser.form.isAttachedToParentUserOptions.${value}`),
        value,
      })),
    [t],
  );

  const occupationTypeOptions = useMemo(
    () =>
      Object.values(EOccupationType).map((value) => ({
        label: t(`pages.currentProfiles.profile.financial.occupationTypes.${value}`),
        value,
      })),
    [t],
  );

  const occupationCategoryOptions = useMemo(
    () =>
      occupationCategoryValues.map((occupation) => ({
        label: t(`pages.dashboard.createUser.form.occupation.options.${occupation}`),
        value: occupation,
      })),
    [t],
  );

  const personalAssetRanges = useMemo(
    () =>
      personalAssetsValues.map((value) => ({
        label: t(`pages.dashboard.createUser.form.personalAssetsEstimated.options.${value}`),
        value,
      })),
    [t],
  );

  const monthlyIncomeRangeOptions = useMemo(
    () =>
      monthlyIncomeRangeValues.map((value) => ({
        label: t(`pages.dashboard.createUser.form.netMontlyIncome.options.${value}`),
        value,
      })),
    [t],
  );

  const sourceOfFundsOptions = useMemo(
    () =>
      sourceOfFunds.map((source) => ({
        label: t(`pages.dashboard.createUser.form.sourceOfFunds.${source}`),
        value: source,
      })),
    [t],
  );

  return {
    countryCodeOptions,
    legalFormOptions,
    entityTypeOptions,
    titleOptions,
    legalSectorTypeOptions,
    incomeRangeOptions,
    personalAssetOptions,
    legalAnnualTurnOverOptions,
    legalNetIncomeRangeOptions,
    legalNumberOfEmployeeRangeOptions,
    sanctionEmbargoQuestionaireOptions,
    persoAndBusinessAddressOptions,
    occupationTypeOptions,
    persoAndParentAddressOptions,
    userHierarchicalRelationOptions,
    occupationCategoryOptions,
    personalAssetRanges,
    monthlyIncomeRangeOptions,
    sourceOfFundsOptions,
  };
}
